import React, { useEffect, useMemo, useState, useRef, KeyboardEvent, ChangeEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { showSuccess, showError } from "toast/toast";
import { Row } from "@tanstack/react-table";
import DataTable from "components/DataTable";
import PaginationControls from "components/PaginationControls";
import SignageAddGroup from "./SignageAddGroup";
import SignageEditGroup from "./SignageEditGroup";
import TablePaginationControls from "components/TableandmobilePaginationcontrol"
import { DesktopTableParts, MobileCardsOnly } from "components/TableandMobilecomponent";
import {
  DesktopTableLoader,
  MobileCardsLoader,
  DataTableStatus,
} from "components/DataTableLoader";
import { ViewIconButton, EditIconButton, DeleteIconButton } from "components/DatatablesActionIcons";
import DeleteConfirmModal from "components/ToolTips/DeleteConfirmModal";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
  createColumnHelper,
} from "@tanstack/react-table";

import {
  ExportAsExcel,
} from "@siamf/react-export";

import { Table, TableHead, TableRow, TableCell, TableBody } from '@mui/material';
import { Group } from 'types';
import { getAuthCookie } from "../../utils/authCookies";
import { useModuleActionPermissions } from "hooks/useModuleActionPermissions";
import { MODULE_PERMISSION_NAMES } from "utils/moduleActionPermissions";

function fuzzyTextFilter<TData>(
  row: Row<TData>,
  columnId: string,
  filterValue: string
) {
  const raw = row.getValue(columnId);
  const value = (typeof raw === "boolean"
    ? raw
      ? "Active"
      : "Inactive"
    : String(raw)
  ).toLowerCase();

  return value.includes(filterValue.toLowerCase());
}

const columnHelper = createColumnHelper<Group>();

const SignageGroups = () => {
  const [groups, setGroups] = useState<Group[]>([]);
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const token = getAuthCookie("sessionToken");

  const [toastShown, setToastShown] = useState(false);
  const location = useLocation();
  const isSignageTeammember = location.pathname.includes('/signageteammember');
  const baseRoute = isSignageTeammember ? '/signageteammember' : '/signage';

  const { canCreate, canView, canEdit, canDelete } = useModuleActionPermissions(
    MODULE_PERMISSION_NAMES.customer_groups
  );
  const navigate = useNavigate();

  const [globalFilter, setGlobalFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [rowCount, setRowCount] = useState(0);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [deleting, setDeleting] = useState<string | null>(null);
  const [showAddGroupModal, setShowAddGroupModal] = useState(false);
  const [group, setGroup] = useState<Group[]>([]); // Store group list
  const [showEditGroupModal, setShowEditGroupModal] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState<string>(globalFilter ?? "");
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);

  const prevEditModalState = useRef(false);
  const prevAddModalState = useRef(false);


  const handleGroupAdded = (newGroup: Group) => {
    setGroup(prev => [...prev, newGroup]);
    setShowAddGroupModal(false); // Close modal after save
  };

  const fetchGroups = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/customer-groups/datatable`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          page: pagination.pageIndex + 1,
          limit: pagination.pageSize,
          search: globalFilter,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to fetch groups.");
      }

      const result = await response.json();
      setGroups(Array.isArray(result?.data) ? result.data : []);
      setRowCount(result?.total);
    } catch (err) {
      showError("Failed to load groups.");
      setError(err instanceof Error ? err.message : "An unknown error occurred");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const justClosedEdit = prevEditModalState.current && !showEditGroupModal;
    const justClosedAdd = prevAddModalState.current && !showAddGroupModal;

    if (justClosedEdit || justClosedAdd) {
      fetchGroups(); // ✅ call API when either modal just closed
    }

    prevEditModalState.current = showEditGroupModal;
    prevAddModalState.current = showAddGroupModal;
  }, [showEditGroupModal, showAddGroupModal]);

  useEffect(() => {
    fetchGroups();
  }, [token, globalFilter]);

  const handleDeleteClick = (group: Group) => {
    setDeleteTarget({ id: group._id, name: group.name || "Group" });
  };

  const handleDeleteCancel = () => {
    setDeleteTarget(null);
    setDeleting(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    await handleDelete(deleteTarget.id);
    setDeleteTarget(null);
  };

  const handleDelete = async (groupId: string) => {
    setLoading(true);
    setDeleting(groupId);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/customer-groups/${groupId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) throw new Error("Delete failed");

      // Remove deleted group from state to update UI
      setGroups(prevGroups => prevGroups.filter(group => group._id !== groupId));

      showSuccess("Group deleted successfully.");
    } catch (err) {
      showError("Failed to delete group.");
      setError(err instanceof Error ? err.message : "An unknown error occurred");
    } finally {
      setLoading(false);
      setDeleting(null);
    }
  };


  const columns = useMemo(() => {
    return [
      columnHelper.display({
        id: "serial",
        header: () => <div className="th-text text-center S-No">S.No.</div>,
        cell: (info) =>
          <div className="text-sm text-center S-No">
            {pagination.pageIndex * pagination.pageSize + info.row.index + 1}
          </div>,
      }),
      columnHelper.accessor(
        (row: Group) => `${row.name}`,
        {
          id: "fullName",
          header: () => <div className="th-text text-center S-No-2">Group Name</div>,
          cell: (info) =>
            <div className="text-sm text-center S-No-2">{info.getValue()}</div>,
          filterFn: fuzzyTextFilter,
          enableGlobalFilter: true,
        }
      ),
      columnHelper.accessor("status", {
        header: () => <div className="th-text text-center">Status</div>,
        cell: info => {
          const value = info.getValue();
          const isActive = value === "Active";

          // pick one of two marker-colors
          const className = isActive
            ? "status-radio status-radio-active"
            : "status-radio status-radio-inactive";

          return (
            <div className="text-sm text-center">
              <input
                type="radio"
                className={className}
                checked={true}
                readOnly
              />
            </div>
          );
        },
        enableGlobalFilter: false,
      }),

      // columnHelper.accessor("status", {
      //   header: () => <div className="th-text text-center">Status</div>,
      //   cell: (info) => {
      //     const status = info.getValue();
      //     const isActive = status.toLowerCase() === "active";
      //     return (
      //       <div className="text-sm text-center">
      //         <span className={`status-badge ${isActive ? "status-accepted-btn" : "status-draft-btn"}`}>
      //           {status}
      //         </span>
      //       </div>
      //     );
      //   },
      //   enableGlobalFilter: true,
      // }),
      columnHelper.display({
        id: "actions",
        header: () => <div className="th-text text-center">Actions</div>,
        cell: (info) => {
          const group = info.row.original;
          return (
            <div className="d-flex align-items-center justify-content-center gap-2">
              {canView && (
              <ViewIconButton
                to={`${baseRoute}/view-group/${group._id}`}
                className="btn action-btn " title="View"
              />
              )}
              {canEdit && (
              <EditIconButton
                type="button"
                className="icon-square"
                title="Edit"
                onClick={() => {
                  setSelectedGroupId(group._id);
                  setShowEditGroupModal(true);
                }}
              />
              )}

              {canDelete && (
              <DeleteIconButton
                type="button"
                className="icon-square danger"
                title="Delete"
                onClick={() => handleDeleteClick(group)}
                disabled={deleting === group._id}
              />
              )}

            </div>
          );
        },
      }),
    ];
  }, [deleting, canView, canEdit, canDelete]);



  const table = useReactTable({
    data: groups,
    columns,
    pageCount: Math.ceil(rowCount / pagination.pageSize),
    state: {
      pagination,
      globalFilter,
    },
    manualPagination: true,
    manualFiltering: true,
    onPaginationChange: setPagination,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
  });

  const pageCount = table.getPageCount();
  const currentPage = table.getState().pagination.pageIndex;

  return (
    <div className="content position-relative">

      <div className="content container">

        <div className="d-flex justify-content-between align-items-center pb-3 mb-2 py-2">
          <h4 className="mb-0 "></h4>
          <div className="d-flex gap-3 align-items-center">
            {/* <button
              className="btn btn-primary  table-addbutton"
              onClick={() => setShowAddGroupModal(true)}
            >
              + Add Group
            </button> */}
          </div>
        </div>
        <div className="po-header mb-4 shadow-border-datatables ">
          <div className="po-top d-flex justify-content-between align-items-start align-items-md-center flex-wrap gap-2">
            <div className="po-title-wrap me-2 d-flex align-items-center">
              <div>
                <h4 className="po-title mb-1">Groups Management</h4>
                <div className="po-subtitle">Manage your organization's groups and teams</div>
              </div>
            </div>

            {/* tools block (search + buttons) */}
            <div className="po-tools d-flex align-items-center flex-wrap gap-2 ms-auto">
              {/* Search */}
              <div className="input-group po-search ">
                <span className="input-group-text">
                  <i className="ri-search-line" />
                </span>
                <input
                  type="text"
                  id="searchInput"
                  name="searchInput"
                  className="form-control"
                  placeholder="Search by Name"
                  value={searchInput}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchInput(e.target.value)}
                  onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                    if (e.key === "Enter") {
                      setGlobalFilter(searchInput);
                    }
                  }}
                  autoComplete="off"
                />
              </div>
              {canCreate && (
                <button
                className="btn btn-primary  table-addbutton"
                onClick={() => setShowAddGroupModal(true)}
              >
                + Add Group
              </button>
              )}
              {/* <ExportAsExcel
                                data={exportRows}
                                headers={[
                                  "Company Name",
                                  "First Name",
                                  "Last Name",
                                  "Phone",
                                  "Email",
                                  "License Status",
                                  "Status",
                                ]}
                                fileName="BrokerageFirms"
                              >
                                {(props) => (
                                  <button {...props} className="export-button">
                                    <i className="ri-file-excel-2-line me-2" />
                                    Export to Excel
                                  </button>
                                )}
                              </ExportAsExcel> */}
            </div>
          </div>
        </div>

        <div className="card overflow-hidden production-card box-shadow-datatables shadow-border-datatables">
          {/* Header like screenshot (left title, right count pill) */}
          <div className="align-items-center bg-white border-bottom  d-flex justify-content-between px-4 py-4">
            <h5 className="mb-0 ">Groups Overview</h5>
            {/* <span className="badge rounded-pill bg-body-tertiary text-secondary-emphasis px-3 py-2">
                        orders
                      </span> */}
          </div>
          <hr className="mb-2" style={{ borderTop: "none" }} />
          {/* DESKTOP (≥ 1022px) */}
          <div className="rdt-desktop ">
            <div
              className="production-scroll"
              style={{ "--rdt-min": "1200px" } as React.CSSProperties}
            >
              <table className="table production-table align-middle mb-0 table-1">
                {loading ? (
                  <DesktopTableLoader table={table} message="Loading groups…" variant="dots" />
                ) : (
                  <DesktopTableParts table={table} />
                )}
              </table>
            </div>
          </div>

          {/* MOBILE (≤ 1021px) */}
          <div className="rdt-mobile">
            {loading ? (
              <MobileCardsLoader message="Loading groups…" variant="dots" />
            ) : (
              <MobileCardsOnly
              table={table}
              idColId="order_link"
              titleColId="product_name"
              actionColId="actions"
            />
            )}
          </div>

        </div>
        <DataTableStatus
          loading={loading}
          isEmpty={table.getRowModel().rows.length === 0}
        >
          <div className="mt-4 mb-4 ">
            <TablePaginationControls table={table} />
          </div>
        </DataTableStatus>
        <DeleteConfirmModal
          open={!!deleteTarget}
          dynamicName={deleteTarget?.name || "Group"}
          onCancel={handleDeleteCancel}
          onDelete={handleDeleteConfirm}
        />
      </div>
      {showEditGroupModal && selectedGroupId && (
        <div
          className="modal fade show d-block"
          tabIndex={-1}
          role="dialog"
          style={{
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1050,
          }}
          onClick={() => setShowEditGroupModal(false)}
        >
          {showEditGroupModal && (
            <div className="modal d-block" onClick={() => setShowEditGroupModal(false)}>
              <div
                className="modal-dialog top-27"
                role="document"
                onClick={(e) => e.stopPropagation()} // prevent backdrop click from closing
              >
                <div className="modal-content" style={{ width: "101%" }}>
                  <div className="modal-body">
                    <SignageEditGroup
                      groupId={selectedGroupId}
                      onClose={() => setShowEditGroupModal(false)}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

        </div>
      )}

      {showAddGroupModal && (
        <div
          className="modal fade show d-block"
          tabIndex={-1}
          role="dialog"
          style={{
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1050,
          }}
          onClick={() => setShowAddGroupModal(false)}
        >
          <div
            className="modal-dialog top-27"
            role="document"
            onClick={(e) => e.stopPropagation()}
          >

            {showAddGroupModal && (
              <div className="modal d-block" onClick={() => setShowAddGroupModal(false)}>
                <div
                  className="modal-dialog top-27"
                  role="document"
                  onClick={(e) => e.stopPropagation()} // prevent closing on inner click
                >
                  <div className="modal-content">
                    <div className="modal-body">
                      <SignageAddGroup
                        onGroupAdd={handleGroupAdded}
                        onClose={() => setShowAddGroupModal(false)}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

          </div>
        </div>
      )}

    </div>
  );

};

export default SignageGroups;
